import React, { useContext, useEffect } from 'react'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { ErrorsService } from 'app/modules/shared/services'
import { routeState } from 'app/modules/shared'
import { RouteState } from 'app/modules/shared/types'
import { AuthContext } from 'app/modules/auth'
import { AuthContextData } from 'app/modules/auth/context'
import { routeToRegexp } from 'app/modules/shared/routing/helpers'
import { useLocation } from 'react-router-dom'
import { trace, isSpanContextValid, SpanStatusCode } from '@opentelemetry/api'
import { ErrorLayout } from './error-layout'

export function InternalErrorPage({ error, resetError }: { error: Error; resetError?: () => void }): JSX.Element {
  const { _t } = useTypedTranslation()
  const pageState = useContext(routeState)
  const auth = useContext(AuthContext)
  const location = useLocation()

  useEffect(() => {
    logError(error, pageState, auth, location)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const handleResetError = () => (resetError ? resetError() : window.location.replace('/'))

  return (
    <ErrorLayout
      code={500}
      message={_t('internal-server-error')}
      note={
        <>
          {_t('error-note1')}
          <br />
          {_t('error-note2')}
          <br />
          {_t('error-note3')}
        </>
      }
      onReset={handleResetError}
    />
  )
}

async function logError(error: Error, pageState: RouteState, auth: AuthContextData, location: any) {
  const url = location.pathname
  const data: { [key: string]: string | boolean } = {
    message: error?.toString() ?? '',
    url: url,
    is_signed_in: !!auth.user,
  }

  if (typeof error?.stack !== 'undefined') {
    data.trace = error.stack
  }

  // Prefer the active span (keeps the error on the same trace as the triggering request).
  // Fall back to a dedicated root span when nothing is active.
  const activeSpan = trace.getActiveSpan()
  const errorSpan = activeSpan ?? trace.getTracer('error-boundary').startSpan('client_error', { root: true })
  errorSpan.recordException(error)
  errorSpan.setStatus({ code: SpanStatusCode.ERROR, message: error?.message })
  const errorSpanCtx = errorSpan.spanContext()
  if (!activeSpan) {
    errorSpan.end()
  }

  if (isSpanContextValid(errorSpanCtx)) {
    data.otel_trace_id = errorSpanCtx.traceId
    data.otel_span_id = errorSpanCtx.spanId
  }

  if (pageState.data.page?.key === 'report') {
    const urlParamsRegexp = routeToRegexp(pageState.data.page.url)
    const params = url.match(urlParamsRegexp)

    if (params?.[1]) {
      data.reportFlowId = params[1]
    }
    if (params?.[2]) {
      data.reportId = params[2]
    }
  }

  try {
    await ErrorsService.sendErrorData(data)
  } catch (e) {
    console.error(e)
  }
}
